import React from "react";
import tw from "twin.macro";
import styled from "styled-components";
import { css } from "styled-components/macro";
import {getPrimaryUrl} from "./helpers/utmHelper";

const Container = tw.div`content-center mt-24 mb-24`;
const SectionDescription = tw.p`mt-4 text-sm md:text-base lg:text-lg font-medium leading-relaxed text-primary-500 max-w-xl font-sans`;
const SectionHeading = tw.h2`text-3xl sm:text-4xl font-black tracking-wide text-center font-sans`
const HeaderContainer = tw.div`flex flex-col items-center`;
const Heading = tw(SectionHeading)`w-full`;
const Description = tw(SectionDescription)`w-full text-center`;
const PlansContainer = tw.div`flex flex-col justify-items-stretch md:justify-center md:flex-row items-center md:items-stretch relative mb-12`;
const Plan = styled.div`
  ${tw`w-full max-w-72 mt-12 md:mr-12 md:last:mr-0 text-center px-8 rounded-lg relative text-primary-500 bg-white flex flex-col shadow-raised`}

  ${props =>
    props.featured &&
    css`
      ${tw`border-2 border-gray-200 shadow-none`}
    `}
`;

const PlanHeader = styled.div`
  ${tw`flex flex-col leading-relaxed py-4 -mx-8 bg-gray-100 rounded-t-lg`}
  .name {
    ${tw`font-bold text-xl`}
  }
  .price {
    ${tw`font-bold text-3xl sm:text-5xl`}
  }
  .duration {
    ${tw`lowercase text-gray-600 font-medium text-sm tracking-widest mb-2`}
  }
  .mainFeature {
    ${tw`text-gray-600 text-sm font-medium tracking-wide`}
  }
`;

const PlanAction = tw.div`px-4 pb-8`;
const PrimaryButtonBase = tw.button`px-1 py-1 text-primary-500 border-3 border-primary-500 border-solid hover:bg-primary-500 hover:text-gray-100 focus:outline-none`;
const PrimaryButton = tw(
    PrimaryButtonBase,
)`mt-4 text-2xl font-bold inline-block mr-2 rounded-xl`;

const PlanBadge = styled.div`
  ${tw`absolute bg-primary-500 text-gray-100 text-xs font-bold px-2 py-1 rounded`}
  top: -0.75rem;
  right: 0.75rem;
`;
const PlanBody = tw.div`px-2 py-4 text-left flex-1`;
const FeaturesList = tw.ul`mt-2 space-y-2`;
const FeatureItem = styled.li`
  ${tw`flex items-start text-sm text-gray-700`}
  svg { ${tw`w-5 h-5 text-green-500 mr-2 flex-shrink-0`} margin-top: 0.125rem; }
`;


// S-all-125 / Q-Marketing-45: the heading sells the never-pay case ("You + your
// AI + anyone's opinions: free forever" — wording picked in the question); the
// description carries the two pricing rules from C-Marketing-210. Per
// Q-Marketing-46 the copy says "your AI", never "unlimited AI agents".
const Pricing =  ({
  isMobile,
  heading = "You + your AI + anyone's opinions: free no credit card",
  description = "Your AI connects over MCP or CLI at no cost. AI is never a paid seat. People you invite are free too, unless you assign jobs to them. And there's no lock-in: the CLI exports all your data.",
}) => {
  const defaultPlans = [
      {
      name: "You + your AI",
      price: 'Free',
      duration: 'No credit card',
      buttonText: 'Start Now',
      buttonUrl: getPrimaryUrl(false),
      mainFeature: "You + your AI + unlimited observers",
      features: [
        'All core features',
        'Your AI connects free over MCP / CLI. Never a paid seat.',
        'Unlimited workspaces',
        'Github actions integration',
        'No lock-in: the CLI exports all your data',
        'Support workspace'
      ]
      },
    {
      name: "You + humans",
      price: "$3.75",
      duration: 'per user/month',
      mainFeature: "6 months free and max $9/month",
      buttonText: 'Start Now',
      buttonUrl: getPrimaryUrl(true),
      featured: true,
      features: [
        'Everything in You + your AI',
        'Free until you assign them work',
        'Slack integration',
        'Use for customer feedback'
      ]
    }
  ];

  return (
    // No scrollSnapAlign here: centering the tall pricing block used to trap the
    // scroll and cut off the footer links below it.
    <Container id='pricing' style={{paddingLeft: '0.5rem', paddingRight: '0.5rem'}}>
        <HeaderContainer>
          {heading && <Heading>{heading}</Heading>}
          {description && <Description>{description}</Description>}
        </HeaderContainer>
        <PlansContainer>
          {defaultPlans.map((plan, index) => (
            <Plan key={index} featured={plan.featured}>
              {plan.featured && (
                <PlanBadge>Future proof</PlanBadge>
              )}
              <PlanHeader>
                  <div>
                      <div className="price">{plan.price}</div>
                      <div className="duration">{plan.duration}</div>
                  </div>
                  <span className="name">{plan.name}</span>
                  <span className="mainFeature">{plan.mainFeature}</span>
              </PlanHeader>
              {plan.features && (
                <PlanBody>
                  <FeaturesList>
                    {plan.features.map((feature, i) => (
                      <FeatureItem key={i}>
                        <svg viewBox="0 0 20 20" fill="currentColor" aria-hidden="true">
                          <path fillRule="evenodd" d="M16.704 5.29a1 1 0 010 1.42l-7.5 7.5a1 1 0 01-1.414 0l-3.5-3.5a1 1 0 111.414-1.42l2.793 2.793 6.793-6.793a1 1 0 011.414 0z" clipRule="evenodd" />
                        </svg>
                        <span>{feature}</span>
                      </FeatureItem>
                    ))}
                  </FeaturesList>
                </PlanBody>
              )}
              {plan.buttonText &&
                (<PlanAction>
                    <PrimaryButton as="a" href={plan.buttonUrl}>
                        {plan.buttonText}
                    </PrimaryButton>
                </PlanAction>)}
            </Plan>
          ))}
        </PlansContainer>
    </Container>
  );
};

export default Pricing;